import "./Modal.scss";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import ReactDOM from "react-dom";
import successIcon from "assets/icons/validation-icon.svg";
import errorIcon from "assets/icons/error.svg";
import errorPopupIcon from "assets/icons/errorpopup.svg";
import { FormattedMessage, useIntl } from "react-intl";
import { QUERY_STATUS } from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import {
  getCustomDescription,
  getCustomTitle,
  handleCustomClass,
} from "utils/modalsHelper";
import davIcon from "assets/icons/dav.svg";

const ValidationModal = ({
  open,
  status = QUERY_STATUS.INITIAL,
  messages = {},
  onClose,
  isCustomModale = false,
  customTitle,
}) => {
  const { lang } = useUserContext();
  const intl = useIntl();

  if (!open || status === QUERY_STATUS.INITIAL) return null;

  const isPending = status === QUERY_STATUS.LOADING;
  const isSuccess = status === QUERY_STATUS.SUCCESS;
  const isError = status === QUERY_STATUS.ERROR;

  const getIcon = () => {
    if (isError) return errorIcon;
    if (isSuccess) return successIcon;
    return null;
  };

  const getTitle = () => {
    const titleConfig = {
      [QUERY_STATUS.LOADING]: {
        id: messages.pendingTitleId || "modal_validation_pending_title",
        defaultMessage:
          messages.pendingTitleDefault || "Traitement en cours...",
      },
      [QUERY_STATUS.ERROR]: {
        id: messages.errorTitleId || "an_error_occured",
        defaultMessage: messages.errorTitleDefault || "Une erreur est survenue",
      },
      [QUERY_STATUS.SUCCESS]: {
        id: messages.successTitleId || "modal_validation_success_title",
        defaultMessage: messages.successTitleDefault || "Succès",
      },
    };

    return intl.formatMessage(
      titleConfig[status] || titleConfig[QUERY_STATUS.SUCCESS],
      messages.values
    );
  };

  const getDescription = ({ messageId = null, messageDefault = null } = {}) => {
    const descriptionConfig = {
      [QUERY_STATUS.LOADING]: {
        id: messages.pendingMessageId || "modal_validation_pending_msg",
        defaultMessage: messages.pendingMessageDefault || "Veuillez patienter…",
      },
      [QUERY_STATUS.ERROR]: {
        id:
          messageId || messages.errorMessageId || "modal_validation_error_msg",
        defaultMessage:
          messageDefault ||
          messages.errorMessageDefault ||
          "Une erreur est survenue lors de l'opération.",
      },
      [QUERY_STATUS.SUCCESS]: {
        id: messages.successMessageId || "modal_validation_success_msg",
        defaultMessage:
          messages.successMessageDefault ||
          "L'opération a été effectuée avec succès.",
      },
    };

    return (
      <FormattedMessage
        {...(descriptionConfig[status] ||
          descriptionConfig[QUERY_STATUS.SUCCESS])}
        values={{
          // Caller-supplied placeholders, spread first so the three below stay
          // available whatever a caller passes.
          ...messages.values,
          br: <br />,
          errorIcon: (
            <img
              src={errorPopupIcon}
              alt="error"
              style={{
                height: "1.4em",
                verticalAlign: "middle",
                display: "inline",
                margin: "0 2px",
              }}
            />
          ),
          nowrap: (chunks) => (
            <span style={{ whiteSpace: "nowrap" }}>{chunks}</span>
          ),
        }}
      />
    );
  };

  return ReactDOM.createPortal(
    <>
      <div className="overlay-style" />
      <div
        className={`validation-modal-style ${handleCustomClass({
          isCustomModale,
          isPending,
          isError,
        })}`}
        id="validation-modal"
      >
        <div className="header">
          {!isPending && (
            <Icon
              id="close-icon"
              color="gray-200"
              icon="close"
              onClick={onClose}
              dataTestId={`${
                isError ? "error" : "success"
              }_validation_modal-close_btn`}
            />
          )}
        </div>
        <div className="body">
          {!isCustomModale || isPending ? (
            <>
              {isPending ? (
                <div className="icon-pending">
                  <img src={davIcon} alt="" className="dav-pending-icon" />
                </div>
              ) : (
                <div
                  className={`
              ${isError && "icon-error"}
              ${isSuccess && "icon-success"}
              `}
                >
                  <div
                    className="validation-icon"
                    style={{ backgroundImage: `url(${getIcon()})` }}
                  />
                </div>
              )}
              <div className="title" id="modal-title">
                {getTitle().toUpperCase()}
              </div>
            </>
          ) : (
            getCustomTitle(intl, customTitle)
          )}
          <div className={`${isCustomModale ? "description-wrapper" : ""}`}>
            {!isCustomModale || isPending ? (
              <div className="description" id="modal-msg">
                {getDescription()}
              </div>
            ) : (
              getCustomDescription({
                customMessages: messages?.customMessages,
                lang,
                getDescription,
                intl,
              })
            )}
          </div>
          <div className="form-buttons">
            {!isPending && (
              <Button
                color="btn-medium gray-700"
                type="button"
                onClick={onClose}
                id="button-form-close"
                data-testid={`${
                  isError ? "error" : "success"
                }_validation_modal-OK_btn`}
              >
                OK
              </Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.getElementById("portal")
  );
};

export default ValidationModal;
